import { Label, Input, fieldClassName, labelClassName, errorTextClassName } from '@shared/ui';
import { USERNAME_PATTERN_MESSAGE } from '@shared/utils/username';
import { sectionHeading } from '../profileStyles';

/**
 * @param {object} props
 * @param {null|'required'|'invalid'|'checking'|'available'|'taken'} [props.usernameStatus] - From useUsernameAvailability.
 *   Shown under Username only while the field has no validation error of its own.
 */
export function PersonalInfoSection({ register, errors, usernameStatus = null, dataTestId }) {
  const baseTestId = dataTestId || 'profile-personal-section';
  return (
    <div className="space-y-4" data-testid={baseTestId}>
      <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Personal Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className={labelClassName} data-testid={`${baseTestId}-firstname-label`}>First Name</Label>
          <Input 
            id="firstName" 
            {...register('firstName')}
            maxLength={64}
            className={fieldClassName}
            aria-invalid={!!errors.firstName}
            data-testid={`${baseTestId}-firstname-input`}
          />
          {errors.firstName && (
            <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-firstname-error`}>{errors.firstName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className={labelClassName} data-testid={`${baseTestId}-lastname-label`}>Last Name</Label>
          <Input 
            id="lastName" 
            {...register('lastName')}
            maxLength={64}
            className={fieldClassName}
            aria-invalid={!!errors.lastName}
            data-testid={`${baseTestId}-lastname-input`}
          />
          {errors.lastName && (
            <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-lastname-error`}>{errors.lastName.message}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="username" className={labelClassName} data-testid={`${baseTestId}-username-label`}>Username</Label>
        <Input 
          id="username" 
          {...register('username')}
          className={fieldClassName}
          aria-invalid={!!errors.username || ['required', 'invalid', 'taken'].includes(usernameStatus)}
          data-testid={`${baseTestId}-username-input`}
        />
        {errors.username && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-username-error`}>{errors.username.message}</p>
        )}
        {!errors.username && usernameStatus === 'required' && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-username-required`}>Username is required.</p>
        )}
        {!errors.username && usernameStatus === 'invalid' && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-username-invalid`}>{USERNAME_PATTERN_MESSAGE}</p>
        )}
        {!errors.username && usernameStatus === 'checking' && (
          <p className="text-sm text-fg-muted mt-1" data-testid={`${baseTestId}-username-checking`}>Checking availability...</p>
        )}
        {!errors.username && usernameStatus === 'taken' && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-username-taken`}>Username is already taken.</p>
        )}
        {!errors.username && usernameStatus === 'available' && (
          <p className="text-sm text-success-fg mt-1" data-testid={`${baseTestId}-username-available`}>Username is available.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className={labelClassName} data-testid={`${baseTestId}-phone-label`}>Phone Number</Label>
        <Input
          id="phone"
          type="tel"
          {...register('phone')}
          maxLength={20}
          className={fieldClassName}
          aria-invalid={!!errors.phone}
          data-testid={`${baseTestId}-phone-input`}
        />
        {errors.phone && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-phone-error`}>{errors.phone.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" className={labelClassName} data-testid={`${baseTestId}-email-label`}>Email Address</Label>
        <Input 
          id="email" 
          type="email" 
          {...register('email')}
          className={`${fieldClassName} text-fg-disabled`} 
          readOnly
          aria-invalid={!!errors.email}
          data-testid={`${baseTestId}-email-input`}
        />
        {errors.email && (
          <p className={`${errorTextClassName} mt-1`} data-testid={`${baseTestId}-email-error`}>{errors.email.message}</p>
        )}
        <p className="text-xs text-fg-muted" data-testid={`${baseTestId}-email-readonly-message`}>Email address cannot be changed.</p>
      </div>
    </div>
  );
}
